/**
 * A smart-prompt review topic.
 *
 * - `display` is the short label shown to the user under the review textarea.
 * - `matchHint` is sent to the LLM (Gemini) alongside the display label so the
 *   model knows precisely what counts as a semantic match. It is never shown
 *   to the user.
 */
export type ReviewTopic = {
  display: string;
  matchHint: string;
};

const SCENT_HINT =
  "Matches text describing how the product smells or what it smells like (smells fresh, lightly scented, strong fragrance, smells like coconut, no smell). Does NOT match how it feels on skin/hair or its results.";

const FEEL_ON_SKIN_HINT =
  "Matches text describing the sensory experience on the skin after application (soft, sticky, hydrated, tight, greasy, smooth, plumped, light). Does NOT match visible results, scent, or the application process.";

const RESULTS_HINT =
  "Matches text describing observed changes or outcomes from using the product over time (clearer skin, fewer breakouts, brighter complexion, no improvement, hair feels stronger after a week). Does NOT match immediate sensory feel, scent, or application experience.";

const APPLICATION_HINT =
  "Matches text describing the experience of applying the product — how easy, smooth, blendable, messy, or quick the application is (glides on, hard to spread, blends easily, leaves streaks). Does NOT match how it feels on skin afterwards or visible results.";

export const BEAUTY_TOPICS: Record<string, ReadonlyArray<ReviewTopic>> = {
  fragrance: [
    {
      display: "Fragrance notes",
      matchHint:
        "Matches text describing the overall character or notes of the scent (floral, woody, sweet, citrus, smells of roses, smells musky). Does NOT match emotional reactions, intensity, or how long it lasts.",
    },
    {
      display: "First impression",
      matchHint:
        "Matches text describing the user's initial reaction or how the fragrance made them feel when they first wore it (loved it instantly, felt confident, reminded me of summer, took me back to my childhood). Does NOT match descriptions of the scent's character itself or its longevity.",
    },
    {
      display: "Intensity",
      matchHint:
        "Matches text describing how strong, faint, subtle, or overpowering the scent is at any given moment (very strong, light and subtle, projects across the room, almost imperceptible). Does NOT match how long the scent lasts.",
    },
    {
      display: "Scent longevity",
      matchHint:
        "Matches text describing how long the scent lingers on skin or clothes after applying (lasted all day, faded after an hour, still smell it the next morning). Does NOT match how long the bottle/product itself lasts as a physical volume of liquid, and does NOT match how strong the scent is.",
    },
  ],
  skincare: [
    { display: "Application", matchHint: APPLICATION_HINT },
    { display: "Feel on skin", matchHint: FEEL_ON_SKIN_HINT },
    { display: "Results", matchHint: RESULTS_HINT },
  ],
  makeup: [
    {
      display: "Coverage",
      matchHint:
        "Matches text describing how much the product conceals or covers the skin (full coverage, sheer, buildable, hides redness, doesn't hide blemishes). Does NOT match finish, shade match, or wear time.",
    },
    {
      display: "Finish",
      matchHint:
        "Matches text describing the final visual appearance of the product on skin (matte, dewy, glowy, natural, satin, shiny). Does NOT match coverage, shade match, or longevity.",
    },
    {
      display: "Shade match",
      matchHint:
        "Matches text describing how well the product's colour matches the user's skin tone or undertone (perfect match, too dark, too pink, oxidises orange, undertone is right). Does NOT match coverage, finish, or general pigmentation.",
    },
    { display: "Application", matchHint: APPLICATION_HINT },
    {
      display: "Staying power",
      matchHint:
        "Matches text describing how the product looks or holds up over hours of wear, OR how long it lasts before needing reapplication (lasted all day, faded by lunch, didn't budge, still looked fresh after 8 hours, transferred onto my mask). Does NOT match initial application, coverage, or finish.",
    },
  ],
  bodycare: [
    { display: "Scent", matchHint: SCENT_HINT },
    { display: "Feel on skin", matchHint: FEEL_ON_SKIN_HINT },
    { display: "Results", matchHint: RESULTS_HINT },
  ],
  haircare: [
    {
      display: "Hair feel",
      matchHint:
        "Matches text describing the sensory state of the hair after use (soft, smooth, frizzy, greasy, light, weighed down, silky, tangled). Does NOT match visible results like shine, scent, or how easy it was to apply.",
    },
    { display: "Scent", matchHint: SCENT_HINT },
    { display: "Results", matchHint: RESULTS_HINT },
  ],
};

export const FOOD_AND_BEVERAGE_TOPICS: Record<
  string,
  ReadonlyArray<ReviewTopic>
> = {
  "chocolate and confectionery": [
    {
      display: "Taste",
      matchHint:
        "Matches text describing the flavour of the product — sweetness, bitterness, saltiness, fruitiness, mintiness, or overall taste balance (too sweet, dark and bitter, perfectly balanced, tastes of caramel, artificial aftertaste). Does NOT match texture, aroma, or how rich/indulgent it feels.",
    },
    {
      display: "Texture",
      matchHint:
        "Matches text describing the physical sensation of eating the product — how it feels in the mouth (smooth, crunchy, chewy, crumbly, melts well, waxy, hard, soft centre). Does NOT match taste or flavour.",
    },
    {
      display: "Richness",
      matchHint:
        "Matches text describing the quality, depth, or indulgence of the chocolate or confectionery (rich, intense, high quality cocoa, luxurious feel, tastes cheap, not very chocolatey, deep flavour). Does NOT match sweetness level or texture.",
    },
  ],
  "snacks and cereals": [
    {
      display: "Taste",
      matchHint:
        "Matches text describing the flavour of the snack or cereal — seasoning, sweetness, saltiness, spice level, or overall taste (too salty, great flavour, tastes bland, nicely seasoned, sweet but not overpowering). Does NOT match texture, crunch, or how filling it is.",
    },
    {
      display: "How filling",
      matchHint:
        "Matches text describing how satisfying or sustaining the product is (kept me full until lunch, not very filling, surprisingly satisfying, left me wanting more, good portion for the calories). Does NOT match taste or texture.",
    },
    {
      display: "Ingredients",
      matchHint:
        "Matches text commenting on ingredient quality, nutritional content, or how natural the product feels (no artificial ingredients, high in sugar, good source of protein, recognisable ingredients). Does NOT match taste or texture.",
    },
  ],
  alcohol: [
    {
      display: "Taste",
      matchHint:
        "Matches text describing the flavour profile when drinking — notes, sweetness, dryness, or bitterness (fruity, oaky, hoppy, crisp, dry, tastes of vanilla). Does NOT match aroma, strength/alcohol level, or aftertaste/finish.",
    },
    {
      display: "Aroma",
      matchHint:
        "Matches text describing how the drink smells before or during consumption (smells hoppy, floral nose, rich aroma, fruity on the nose). Does NOT match the taste itself or the finish/aftertaste.",
    },
    {
      display: "Finish",
      matchHint:
        "Matches text describing what happens after swallowing — the aftertaste, how long the flavour lingers, or how it leaves the palate (long finish, clean finish, bitter aftertaste, warm finish, disappears quickly). Does NOT match the initial taste or aroma.",
    },
    {
      display: "Strength",
      matchHint:
        "Matches text describing how the alcohol level comes through in the experience — whether it tastes or feels strong, weak, smooth, or harsh (quite boozy, can't taste the alcohol, strong burn, smooth for its ABV, very light). Does NOT match flavour notes.",
    },
  ],
  coffee: [
    {
      display: "Taste",
      matchHint:
        "Matches text describing the flavour of the coffee — boldness, bitterness, fruitiness, smoothness, or overall taste balance (too bitter, smooth and balanced, chocolatey notes, tastes acidic, rich and full-bodied). Does NOT match aroma or perceived strength/caffeine kick.",
    },
    {
      display: "Aroma",
      matchHint:
        "Matches text describing how the coffee smells before or while brewing or drinking (incredible aroma, smells fresh, strong coffee smell, not very aromatic, beautiful roasted scent). Does NOT match taste or flavour.",
    },
    {
      display: "Strength",
      matchHint:
        "Matches text describing how strong or mild the coffee feels in terms of intensity or caffeine kick (too strong for me, mild and gentle, good strong coffee, not strong enough). Does NOT match flavour notes.",
    },
  ],
};

export const PET_CARE_TOPICS: Record<string, ReadonlyArray<ReviewTopic>> = {
  "pet food": [
    {
      display: "Pet's response",
      matchHint:
        "Matches text describing whether the pet actually eats the food eagerly or refuses it (my dog loves it, she wouldn't touch it, cats go crazy for it, left it in the bowl, always finishes it). Does NOT match ingredients, digestibility, or coat/health results.",
    },
    {
      display: "Ingredients",
      matchHint:
        "Matches text commenting on the quality or content of ingredients (real meat as first ingredient, no artificial additives, high protein, mostly fillers, 100% natural). Does NOT match how the pet reacts to eating it or the health results.",
    },
    {
      display: "Results",
      matchHint:
        "Matches text describing observed effects on the pet's health, digestion, or coat over time (no stomach issues, caused loose stools, great for sensitive stomachs, coat looks shinier, improved energy). Does NOT match whether the pet likes the taste or ingredient lists.",
    },
  ],
};

export const HEALTH_AND_WELLBEING_TOPICS: Record<
  string,
  ReadonlyArray<ReviewTopic>
> = {
  supplements: [
    {
      display: "Effectiveness",
      matchHint:
        "Matches text describing whether the user noticed a health benefit from taking the supplement (felt more energised, no difference after a month, joints feel better, improved sleep, noticed a real change). Does NOT match ease of taking, taste, or smell.",
    },
    {
      display: "Ease of taking",
      matchHint:
        "Matches text about the format or how easy it is to consume (easy to swallow, capsule is huge, dissolved well, gummies are convenient, good as a powder in a shake). Does NOT match effectiveness or taste.",
    },
    {
      display: "Taste / smell",
      matchHint:
        "Matches text describing the taste or smell of the supplement, particularly for gummies, powders, or chewables (tastes great, awful chalky taste, no taste at all, smells strong, pleasant flavour). Does NOT match effectiveness or ease of swallowing a tablet.",
    },
  ],
};

export const PERSONAL_CARE_TOPICS: Record<
  string,
  ReadonlyArray<ReviewTopic>
> = {
  oral: [
    {
      display: "Taste",
      matchHint:
        "Matches text describing the flavour during use (too minty, pleasant taste, quite mild, strong antiseptic taste, refreshing flavour). Does NOT match results over time or the feeling after use.",
    },
    {
      display: "Results",
      matchHint:
        "Matches text describing noticeable outcomes from using the product over time (teeth feel cleaner, fresher breath, reduced sensitivity, no visible whitening, gums feel healthier). Does NOT match taste or immediate feel.",
    },
    {
      display: "Feel",
      matchHint:
        "Matches text describing the sensory experience during or immediately after use (fresh and clean feeling, harsh on gums, tingly, left a film, mouth feels great). Does NOT match flavour or longer-term results.",
    },
  ],
  deodorant: [
    {
      display: "Protection",
      matchHint:
        "Matches text describing how well the product controls body odour or wetness throughout the day (kept me fresh all day, started to smell by afternoon, great odour protection, let me down during exercise, still dry after 12 hours). Does NOT match how the deodorant itself smells on application or how it feels on skin.",
    },
    {
      display: "Scent",
      matchHint: SCENT_HINT,
    },
    {
      display: "Feel on skin",
      matchHint:
        "Matches text describing the physical sensation when applied or any residue left behind (left white marks on clothes, smooth and non-sticky, rolled on easily, feels greasy, irritated my skin, no residue). Does NOT match how the deodorant smells or its all-day protection performance.",
    },
  ],
  grooming: [
    {
      display: "Results",
      matchHint:
        "Matches text describing the visible or tactile outcome of using the product — closeness of shave, skin smoothness, beard condition, or hair removal results (super close shave, still had stubble, left my skin smooth, beard feels soft and conditioned, missed patches). Does NOT match how it feels during application or its scent.",
    },
    {
      display: "Scent",
      matchHint:
        "Matches text describing how the product smells during or after use (smells great, pleasant aftershave scent, strong masculine fragrance, unscented which I prefer, overpowering). Does NOT match shaving or grooming performance or how it feels on skin.",
    },
    {
      display: "Feel on skin",
      matchHint:
        "Matches text describing the skin experience during and after use — comfort, irritation, or moisturising effect (no razor burn, caused redness and irritation, left skin feeling smooth and moisturised, stung after shaving, very soothing). Does NOT match the quality of the shaving result or the product's scent.",
    },
  ],
  "intimate care": [
    {
      display: "Gentleness",
      matchHint:
        "Matches text describing how gentle or irritating the product is on sensitive skin (no irritation at all, caused itching or a reaction, gentle enough for daily use, kind to delicate skin, didn't react at all). Does NOT match freshness or odour control, or the physical sensation during use.",
    },
    {
      display: "Freshness",
      matchHint:
        "Matches text describing how effectively the product provides a clean or fresh feeling, or controls odour (felt fresh all day, great at controlling odour, didn't feel particularly clean, lovely fresh feeling, no lingering smell). Does NOT match gentleness on skin or the physical texture/feel during use.",
    },
    {
      display: "Feel",
      matchHint:
        "Matches text describing the physical experience of using the product — texture, moisture level, or comfort during use (soft and gentle, not too wet, leaves skin feeling dry, comfortable to wear, cooling sensation, quite thick). Does NOT match skin sensitivity reactions or freshness and odour control.",
    },
  ],
};

export const MUM_AND_BABY_TOPICS: Record<string, ReadonlyArray<ReviewTopic>> = {
  nappies: [
    {
      display: "Leak protection",
      matchHint:
        "Matches text describing how well the nappy contains liquid and prevents leaks (no leaks overnight, leaked at the legs, great absorbency, soaked through). Does NOT match fit, comfort, or ease of use.",
    },
    {
      display: "Fit and comfort",
      matchHint:
        "Matches text describing how the nappy fits the baby and whether it causes any irritation (fits snugly, left red marks, good range of sizes, tabs kept coming undone, rubs on the legs). Does NOT match absorbency or ease of fastening.",
    },
    {
      display: "Ease of use",
      matchHint:
        "Matches text about the changing experience — how easy the nappy is to put on, fasten, or remove (easy to fasten, tricky to get on a wriggly baby, tabs are sturdy, straightforward to change). Does NOT match fit on the baby or leak performance.",
    },
  ],
  food: [
    {
      display: "Baby's reaction",
      matchHint:
        "Matches text describing whether the baby enjoys eating the food (my baby loves it, refused to eat it, always finishes the whole pouch, wolfed it down). Does NOT match ingredients or texture/consistency.",
    },
    {
      display: "Ingredients",
      matchHint:
        "Matches text commenting on ingredient quality, nutritional content, or how natural the product is (no added sugar, 100% organic, real fruit, too many fillers, clean ingredients). Does NOT match whether the baby likes it or texture.",
    },
    {
      display: "Texture / consistency",
      matchHint:
        "Matches text describing the physical texture or consistency of the food and whether it suits the baby's age or stage (smooth enough for a 6-month-old, good chunky texture for older babies, too lumpy, perfectly pureed). Does NOT match taste preferences or ingredients.",
    },
  ],
};

export const HOUSEHOLD_TOPICS: Record<string, ReadonlyArray<ReviewTopic>> = {
  "laundry products/fabric softeners": [
    {
      display: "Cleaning performance",
      matchHint:
        "Matches text describing how well the product cleans clothes — stain removal, freshness, or overall wash results (removes tough stains, clothes come out fresh, didn't shift the grease, leaves clothes looking dull). Does NOT match scent or gentleness on fabrics.",
    },
    {
      display: "Scent",
      matchHint:
        "Matches text describing the fragrance of the product during washing or on clothes afterwards (beautiful scent, lasts for days, too overpowering, no scent at all, fresh smell). Does NOT match cleaning performance or gentleness.",
    },
    {
      display: "Gentleness",
      matchHint:
        "Matches text about the effect on fabrics, colours, or sensitive skin (colours stayed bright, gentle on delicates, caused skin irritation, faded the fabric, safe for sensitive skin). Does NOT match scent or cleaning performance.",
    },
  ],
  "bleaches & disinfectants": [
    {
      display: "Effectiveness",
      matchHint:
        "Matches text describing how well the product kills germs, removes stains, or cleans surfaces (shifted the stains instantly, didn't notice much difference, genuinely disinfects, got rid of mould). Does NOT match scent or ease of use.",
    },
    {
      display: "Scent",
      matchHint:
        "Matches text describing the smell during and after use (overpowering bleach smell, fades quickly, faint and tolerable, strong chemical smell). Does NOT match effectiveness or application experience.",
    },
    {
      display: "Ease of use",
      matchHint:
        "Matches text about application, whether dilution is needed, or packaging (easy to apply, tricky bottle, needed to dilute a lot, spray works well, good nozzle). Does NOT match effectiveness or scent.",
    },
  ],
  "kitchen towels/paper tissues/toilet tissue": [
    {
      display: "Feel",
      matchHint:
        "Matches text describing softness or texture against skin (soft and gentle, quite rough, fine for everyday use, feels premium, scratchy). Does NOT match how long it lasts or how well it absorbs.",
    },
    {
      display: "Durability",
      matchHint:
        "Matches text describing whether the product holds up under use — especially when wet (strong when wet, disintegrated easily, held up well, falls apart, doesn't tear). Does NOT match softness or absorbency.",
    },
    {
      display: "Absorbency",
      matchHint:
        "Matches text describing how well the product soaks up liquid or moisture (very absorbent, doesn't soak up much, great for spills, soaks through quickly). Does NOT match softness or durability.",
    },
  ],
  "washing-up products": [
    {
      display: "Cleaning power",
      matchHint:
        "Matches text describing how effectively the product cuts through grease or cleans dishes (cuts through grease easily, needed loads of it, doesn't lather well, very effective on baked-on food). Does NOT match scent or ease of use.",
    },
    {
      display: "Scent",
      matchHint:
        "Matches text describing the fragrance during or after washing up (lovely fresh scent, overpowering, no smell, pleasant lemon scent). Does NOT match cleaning effectiveness or ease of use.",
    },
    {
      display: "Ease of use",
      matchHint:
        "Matches text about how easy the product is to use — lathering, how much is needed, how it applies and rinses (a little goes a long way, have to use a lot, rinses off easily, good lather). Does NOT match cleaning results or scent.",
    },
  ],
  stationery: [],
};

export const HOUSEHOLD_SUPPLIES_TOPICS: Record<
  string,
  ReadonlyArray<ReviewTopic>
> = {
  "cleaning appliances": [
    {
      display: "Performance",
      matchHint:
        "Matches text describing how well the appliance cleans (picks up everything, leaves floors spotless, didn't clean as well as expected, powerful suction, great at removing dirt). Does NOT match ease of use or scent.",
    },
    {
      display: "Ease of use",
      matchHint:
        "Matches text about how straightforward the appliance is to operate, set up, or maintain (easy to assemble, complicated to set up, lightweight and easy to manoeuvre, tricky to empty). Does NOT match cleaning performance or scent.",
    },
    {
      display: "Scent",
      matchHint:
        "Matches text describing any fragrance produced during or after use — from the appliance itself, its pads, or cleaning solution (leaves a fresh scent, smells clean, no smell at all, unpleasant chemical smell during use). Does NOT match cleaning performance or ease of use.",
    },
  ],
  "bleaches and disinfectants": [
    {
      display: "Effectiveness",
      matchHint:
        "Matches text describing how well the product kills germs, removes stains, or cleans surfaces (shifted the stains instantly, didn't notice much difference, genuinely disinfects, got rid of mould). Does NOT match scent or ease of use.",
    },
    {
      display: "Scent",
      matchHint:
        "Matches text describing the smell during and after use (overpowering bleach smell, fades quickly, faint and tolerable, strong chemical smell). Does NOT match effectiveness or application experience.",
    },
    {
      display: "Ease of use",
      matchHint:
        "Matches text about application, whether dilution is needed, or packaging (easy to apply, tricky bottle, needed to dilute a lot, spray works well, good nozzle). Does NOT match effectiveness or scent.",
    },
  ],
  stationary: [],
};

export const PHARMACEUTICALS_TOPICS: Record<
  string,
  ReadonlyArray<ReviewTopic>
> = {
  pharmaceuticals: [
    {
      display: "Effectiveness",
      matchHint:
        "Matches text describing whether the product worked for its intended purpose (relieved my headache within the hour, didn't notice any effect, worked better than expected, quick relief). Does NOT match ease of taking or format.",
    },
    {
      display: "Ease of taking",
      matchHint:
        "Matches text about the format, size, or how easy the product is to consume (easy to swallow, tablet is quite large, dissolves quickly, good liquid option for children, capsules are small). Does NOT match effectiveness.",
    },
  ],
};

export const ALL_REVIEW_TOPICS: Record<
  string,
  Record<string, ReadonlyArray<ReviewTopic>>
> = {
  beauty: BEAUTY_TOPICS,
  "food and beverage": FOOD_AND_BEVERAGE_TOPICS,
  "pet care": PET_CARE_TOPICS,
  "health and wellbeing": HEALTH_AND_WELLBEING_TOPICS,
  "personal care": PERSONAL_CARE_TOPICS,
  "mum and baby": MUM_AND_BABY_TOPICS,
  household: HOUSEHOLD_TOPICS,
  "household supplies": HOUSEHOLD_SUPPLIES_TOPICS,
  pharmaceuticals: PHARMACEUTICALS_TOPICS,
};

/**
 * Campaign IDs that have smart prompts enabled regardless of the sub-organisation's
 * licence tier. Add IDs here as a short-term measure while the licence is being set up.
 * Remove IDs once the licence is in place.
 */
const SMART_PROMPTS_CAMPAIGN_ALLOWLIST: ReadonlySet<string> = new Set([
  // CMP-LOCCIT-6
  "85973d0d-0606-40bd-b00b-de16b3508032",
  // CMP-LOCCBE-11
  "5e65094f-be0d-404d-9745-d3455a1a9943",
  // CMP-LOCCFR-41
  "8669d24e-dd80-4726-a5e0-d11bb1e44e52",
  // CMP-LOCCES-3
  "57fd6ae4-ab53-480c-87bb-1cd4a875b924",
  // CMP-DAVEGB-44 — STAGING TESTING ONLY, remove when done
  "2e0e7add-e2f8-446d-8d5d-495afe68bc91",
  // requests in HDT https://app.intercom.com/a/inbox/rw2u0phv/inbox/conversation/215475688928752
  "3509afa4-a10d-4e02-b296-741d79bbc735",
  "c7226f47-162b-4b2e-a7a8-37eec4b4721c",
  // request in HDT https://app.intercom.com/a/inbox/rw2u0phv/inbox/conversation/215475824502956
  "f4c3f4ed-37be-4e1f-a4c1-39b24065f224",
]);

/**
 * Returns true if smart prompts should be shown for this campaign.
 * This is the case when the sub-organisation has an active premium licence,
 * OR when the campaign ID is in the temporary allowlist above.
 */
export function isSmartPromptsEligible(
  isPremium: boolean,
  campaignId: string,
): boolean {
  return isPremium || SMART_PROMPTS_CAMPAIGN_ALLOWLIST.has(campaignId);
}

// Uses the last but one hex digit of the ID to determine whether to hide or show topics,
// with values 0-7 showing topics and 8-f hiding topics
export function hideForABTest(orderId?: string | null): boolean {
  if (!orderId) return false;
  const lastChar = orderId.replace(/-/g, "").slice(-2, -1).toLowerCase();
  return parseInt(lastChar, 16) >= 8;
}

export function getReviewTopicsForCampaign(
  category: string,
  subcategory: string,
): ReadonlyArray<ReviewTopic> {
  return (
    ALL_REVIEW_TOPICS[category.toLowerCase()]?.[subcategory.toLowerCase()] ?? []
  );
}

export type SmartPromptsTrackingPayload = {
  metadata: {
    smart_prompts: {
      prompts: string[];
      shown_count: number;
      category: string;
      subcategory: string;
    };
  };
};

export function buildSmartPromptsPayload(
  category: string,
  subcategory: string,
): SmartPromptsTrackingPayload | null {
  if (subcategory.toLowerCase() === "other") return null;

  const topics = getReviewTopicsForCampaign(category, subcategory);
  const shown = topics.length > 0;

  if (shown) {
    return {
      metadata: {
        smart_prompts: {
          prompts: topics.map((t) => t.display),
          shown_count: topics.length,
          category,
          subcategory,
        },
      },
    };
  }

  return null;
}
